import { memo } from "react";
import { LuHeart } from "react-icons/lu";
import AddToCartButton from "./AddToCartButton";
import EProteinScore from "./EProteinScore";
import HealthBadge from "./HealthBadge";
import NutriScore from "./NutriScore";
import PriceDisplay from "./PriceDisplay";
import ProductImage from "./ProductImage";

const MACROS = [
  ["protein", "Protein", "g"],
  ["carbs", "Carbs", "g"],
  ["fat", "Fats", "g"],
  ["calories", "Calories", ""],
];

const macroText = (value, unit) => {
  if (value === null || value === undefined) return "—";
  // Whole-kit amounts run into hundreds of grams: no decimals there.
  return unit && value < 100 ? `${Number(value.toFixed(1))}${unit}` : `${Math.round(value)}${unit}`;
};

const formatKitWeight = (grams) => (grams >= 1000 ? `${Number((grams / 1000).toFixed(2))} kg` : `${Math.round(grams)} g`);

/** One product in the listing: everything comes from the storefront API card. */
function ProductCard({ product, favorite, onOpen, onToggleFavorite, onMessage }) {
  const { nutrition, pack } = product;
  // Kits show what one whole kit holds, not 100 g of it.
  const kit = nutrition?.perKit ?? null;
  const macros = kit ?? nutrition?.per100g;

  return (
    <article
      className="ep-card"
      onClick={() => onOpen(product.productId)}
      onKeyDown={(e) => e.key === "Enter" && e.target === e.currentTarget && onOpen(product.productId)}
      tabIndex={0}
      aria-label={`${product.name}${product.localName ? `, ${product.localName}` : ""}`}
    >
      <div className="ep-card-media">
        <ProductImage path={product.imagePath} alt={product.name} />
        {product.badge && <span className="ep-ribbon">{product.badge}</span>}
      </div>

      <div className="ep-card-body">
        <h2 className="ep-card-name">
          {product.name}
          {product.localName && (
            <span className="ep-local" lang="te">
              ({product.localName})
            </span>
          )}
        </h2>

        {nutrition ? (
          <>
            <div className="ep-row is-spread">
              <NutriScore grade={nutrition.health.nutriScore} />
              <HealthBadge nutrition={nutrition} />
            </div>
            <div className="ep-macros" aria-label={kit ? "Whole kit" : "Per 100 g"}>
              {MACROS.map(([key, label, unit]) => (
                <div key={key} className={`ep-macro ${key === "protein" ? "is-key" : ""}`}>
                  <b>{macroText(macros?.[key], unit)}</b>
                  <span>{label}</span>
                </div>
              ))}
            </div>
            {kit && <p className="ep-macro-basis">Whole kit{kit.grams ? ` · ${formatKitWeight(kit.grams)}` : ""}</p>}
          </>
        ) : (
          <p className="ep-unavailable">Nutrition information unavailable</p>
        )}
      </div>

      <div className="ep-card-score">
        {nutrition && <EProteinScore score={nutrition.health.eatProteinScore} />}
      </div>

      <div className="ep-card-foot">
        <PriceDisplay pack={pack} />
        <AddToCartButton
          priceId={pack?.priceId}
          productName={product.name}
          packLabel={pack?.label}
          disabled={!product.inStock}
          onMessage={onMessage}
        />
      </div>

      <button
        type="button"
        className={`ep-icon-btn ep-card-fav ${favorite ? "is-active" : ""}`}
        aria-label={favorite ? `Remove ${product.name} from favorites` : `Add ${product.name} to favorites`}
        aria-pressed={favorite}
        onClick={(e) => {
          e.stopPropagation();
          onToggleFavorite(product.productId);
        }}
      >
        <LuHeart fill={favorite ? "currentColor" : "none"} />
      </button>
    </article>
  );
}

export default memo(ProductCard);
